import { getAllFromController } from "./api"
import qs from "querystringify"

/**
 * Filter array by a specified fieldToFilterBy
 * @param {array} array The array to filter
 * @param {string} inputValue Input used to filter the array
 * @param {string} fieldToFilterBy The field used to filter
 * @returns {array} The filtered array
 */
export const filterArrayByField = (data, inputValue = "", fieldToFilterBy = "label") => {
  return data.filter((i) => i[fieldToFilterBy].toLowerCase().includes(inputValue.toLowerCase()))
}

/**
 * Sort an array by a specified sortingField
 * @param {array} array The array to sort
 * @param sortingField The field used to sort the array
 * @param {boolean} ascending In ascending order
 * @returns A sorted array
 */
export const sortOrderArray = (array, sortingField, ascending = true) => {
  return array.sort((itemA, ItemB) => {
    if (ascending) {
      return itemA.order[sortingField] > ItemB.order[sortingField] ? 1 : -1
    } else {
      return itemA.order[sortingField] < ItemB.order[sortingField] ? 1 : -1
    }
  })
}

/**
 * Format the dataset for react-select component
 * @param {Array} dataset Dataset to format
 * @param {String} value Item's property to use as value
 * @param {Array} labels Array of strings, usable properties to use as labels (concat)
 * @returns {Array} Formatted dataset
 * @example
 * formatForSelect([{id: 1, name: "Jean"}, {id: 2, name: "Frank Dupont"}]) // [{value: 1, label: "Jean"}, {value: 2, label: "Frank Dupont"}]
 */
export const formatForSelect = (dataset, value = "id", labels = ["name"]) => {
  const formatedValues = []
  /* eslint-disable implicit-arrow-linebreak */
  dataset?.data.map((item) => {
    // loop in the array to check if the item has those properties
    let labelsToUse = ""
    labels.map((label) => {
      if (item[label]) {
        labelsToUse += `${item[label]} `
      }
      if (label === "addresses[0].postCode" && item.addresses[0]) {
        labelsToUse += `— ${item.addresses[0].postCode} ${
          item.addresses[0].city ? item.addresses[0].city.name : ""
        } `
      }
    })
    formatedValues.push({
      value: item[value],
      label: labelsToUse.trim()
    })
  })
  return formatedValues
}

/**
 * Format a date in ISO (YYYY-MM-DD) for display purposes
 * @param {Date} dateToFormat Date to format
 * @param {boolean} EU EU display DD-MM-YYYY ?
 * @param {boolean} timestamp Include time ?
 * @returns An ISO formated Date
 */
export const dateISO = (dateToFormat = new Date(), EU = false, timestamp = false) => {
  const separator = EU ? "." : "-"
  let date
  dateToFormat = new Date(dateToFormat)

  const day = dateToFormat.getDate()
  const month = dateToFormat.getMonth() + 1
  const year = dateToFormat.getFullYear()
  if (EU) {
    date = `${day < 10 ? `0${day}` : `${day}`}${separator}${
      month < 10 ? `0${month}` : `${month}`
    }${separator}${year}`
  } else {
    date = `${year}${separator}${month < 10 ? `0${month}` : `${month}`}${separator}${
      day < 10 ? `0${day}` : `${day}`
    }`
  }
  if (timestamp) {
    const hours = dateToFormat.getHours()
    const minutes = `0${dateToFormat.getMinutes()}`.slice(-2)
    return `${date} ${hours}:${minutes}`
  } else {
    return date
  }
}

/**
 * Generates a UTC zoned ISO formatted date string
 * @param {string} dateString - Text string containing an accepted date format
 * @returns {date} ISO string formatted date for UTC time
 */
export const getISODateUTC = (dateString = new Date()) => {
  const offset = new Date().getTimezoneOffset()
  const offsetInMs = offset * 60 * 1000
  const myDate = Date.parse(dateString) - offsetInMs
  const dateAsISO = new Date(myDate).toISOString()
  return dateAsISO
}

/**
 * Test if the email is correctly formated
 * @param {String} email Email to check
 * @returns {Boolean} True or false
 */
export const validateEmail = (email) => {
  const regEx =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return regEx.test(email)
}
/**
 * Test if the phone is correctly formated
 * @param {String} phone Phone to check
 * @returns {Boolean} True or false
 */
export const validatePhone = (phone) => {
  const regEx = /^[0]{0,2}[0-9]{7,15}$/
  return regEx.test(phone)
}

/**
 * Fetch and format values to provide to a react-select
 * @param {Object} controller Controller name and its endpoint {name : "", apiEndpoint : ""}
 * @param {Array} labelKey Name(s) of key to display ["name", "iso3"]
 * @returns {Array} Formatted dataset
 */
export const provideOptionsToSelect = async (controller, labelKey = ["name"]) => {
  const data = await getAllFromController(controller.name, controller.apiEndpoint)
  return formatForSelect(data, undefined, labelKey)
}

/**
 * Split an array into two following a condition
 * @param {Array} array The array to split
 * @param filter The condition to split
 * @returns {Array} Two arrays
 */
export const partition = (array, filter) => {
  const pass = [],
    fail = []
  array.forEach((e, idx, arr) => (filter(e, idx, arr) ? pass : fail).push(e))
  return [pass, fail]
}

/**
 * Sleep for a specified time
 * @param {Int} time Time to sleep (ms)
 * @returns {Void} Nothing
 */
export const sleep = (time) => {
  return new Promise((resolve) => setTimeout(resolve, time))
}

/**
 * Get cookies from the browser
 * @param {String} cookieName Cookie name to search for
 * @param {Boolean} convertToInt Parse the value as int ?
 * @returns {Any} Cookie value
 */
export const getCookie = (cookieName, convertToInt = true) => {
  const cookieValue = document.cookie.match(`(^|;)\\s*${cookieName}\\s*=\\s*([^;]+)`)?.pop() || null
  return convertToInt ? parseInt(cookieValue) : cookieValue
}

/**
 * Get the color of an event depending on its reason
 * @param {Int} eventReasonId Event reason id
 * @returns {String} Color name
 * @example
 * eventColorSelection(1) // "success"
 */
export const eventColorSelection = (eventReasonId) => {
  // more flexible and cleaner than a switch
  if (eventReasonId < 10) return "dark"
  if (eventReasonId < 50) return "success"
  if (eventReasonId < 60) return "info"
  if (eventReasonId < 70) return "primary"
  if (eventReasonId < 80) return "warning"
  return "danger"
}

/**
 * Get the currency of a given country
 * @param {Int} countryId Country id
 * @returns {Object} {"id": 29,"iso3": "CHF","num": 756,"name": "Swiss franc"}
 */
export const getCurrencyFromCountry = async (countryId) => {
  let currencyId = 47 // default to EUR
  if (countryId === 125 || countryId === 214) currencyId = 29 // Lichtenstein or Switzerland -> CHF
  if (countryId === 45) currencyId = 32 // China -> CNY
  const currency = await getAllFromController(`Currencies/${currencyId}`, "billing")
  return currency
}

/**
 * Convert a given name into a bs-color based on the postion of the initial in the alphabet
 * @param {String} name Name to convert
 * @returns {String} Color name
 */
export const convertNameToColor = (name) => {
  const initial = name[0].toUpperCase()
  const colorArray = ["success", "danger", "warning", "info", "primary", "secondary", "dark"]
  const letterIndex = [...initial].map((a) => parseInt(a, 36) - 10).filter((a) => a >= 0)
  return colorArray[parseInt((letterIndex - 1) / (26 / colorArray.length))]
}

/**
 * Basic check if field with given key is empty
 * @param {*} data Data to check
 * @returns {Boolean} True or false
 */
export const checkFieldValidity = (data) => {
  let fieldValue = data
  if (typeof data === "object" && data !== null) {
    // if object, dig for value
    fieldValue = data.value
  }
  if (Array.isArray(data) && data.length > 0) {
    // if array, set array length as value
    fieldValue = data.length
  }
  // test if field is empty
  if (
    fieldValue === null ||
    fieldValue === undefined ||
    fieldValue?.length === 0 ||
    fieldValue === "" ||
    (typeof fieldValue === "string" && fieldValue.trim() === "")
  ) {
    return false
  } else {
    return true
  }
}

export function humanFileSize(size) {
  const i = size === 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024))
  return `${(size / Math.pow(1024, i)).toFixed(2) * 1} ${["B", "kB", "MB", "GB", "TB"][i]}`
}

export function formatNumber(number) {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'")
}

export function roundToX(number, x, direction) {
  if (direction === "up") {
    return Math.ceil(number / x) * x
  }
  if (direction === "down") {
    return Math.floor(number / x) * x
  }
  return Math.round(number / x) * x
}

//---------------------------------------------------------------------------------------------------------------------

const urlParser = (requestData) => {
  if (requestData.params) {
    for (const param in requestData.params) {
      requestData.url = requestData.url.replace(`{${param}}`, requestData.params[param])
    }
  }
  if (requestData.qs) requestData.url = requestData.url.concat(qs.stringify(requestData.qs, true))
}
const requestHandler = async (requestData) => {
  try {
    const response = await axios({
      method: requestData.method,
      url: requestData.url,
      data: requestData.data
    })
    return response
  } catch (error) {
    console.error(error)
  }
}

export const testRequestSuccess = (requestData, requestResponse, customErrorMessage) => {
  if (requestResponse.status < 200 || requestResponse.status >= 400) {
    if (customErrorMessage) throw new Exception(customErrorMessage)
    throw new Exception(
      `Error: ${requestData.method} request failure on ${requestData.params.service} - ${requestData.params.controller}`
    )
  }
}
export const requestWrapper = async (requesetData, customErrorMessage) => {
  try {
    urlParser(requesetData)
    const response = await requestHandler(requesetData)
    testRequestSuccess(requestData, response, customErrorMessage)
    return response.data
  } catch (error) {
    console.error(error)
    throw error
  }
}
